import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const GROUP_LABELS = { loads: "Viajes", cruces: "Cruces", cajas: "Cajas", clientes: "Clientes" };
const GROUP_ORDER = ["loads", "cruces", "cajas", "clientes"];

// Búsqueda global ("Ctrl+B del Excel", Parte E). Atajo de teclado "/", mínimo 3 caracteres,
// resultados agrupados por tipo, navega directo al registro respetando permisos del backend.
export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 3) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/buscar", { params: { q: q.trim() } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (group, item) => {
    setOpen(false);
    setQ("");
    if (group === "loads") navigate(`/loads/${item.id}`);
    else if (group === "cruces") navigate(`/cruces?referencia=${encodeURIComponent(item.titulo)}`);
    else if (group === "cajas")
      navigate(`/cajas?tab=movimientos&caja_numero=${encodeURIComponent(item.caja_numero || "")}`);
    else if (group === "clientes") navigate(`/clients?search=${encodeURIComponent(item.titulo)}`);
  };

  const hasResults = results && GROUP_ORDER.some((g) => (results[g] || []).length > 0);

  return (
    <div className="relative w-full lg:max-w-sm" ref={boxRef} data-tour="global-search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Buscar referencia, factura, viaje, caja o cliente… ( / )"
        data-testid="global-search-input"
        className="h-9 w-full rounded-full border border-input bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
      />
      {open && results && (
        <div
          className="absolute z-50 mt-1 max-h-96 w-full overflow-y-auto rounded-md border border-border bg-white shadow-lg"
          data-testid="global-search-results"
        >
          {!hasResults && <div className="px-3 py-3 text-xs text-muted-foreground">Sin resultados.</div>}
          {GROUP_ORDER.map(
            (group) =>
              (results[group] || []).length > 0 && (
                <div key={group}>
                  <div className="bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase text-muted-foreground">
                    {GROUP_LABELS[group]}
                  </div>
                  {results[group].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => go(group, item)}
                      data-testid={`global-search-result-${group}-${item.id}`}
                      className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <span className="text-sm font-medium">{item.titulo}</span>
                      <span className="text-xs text-muted-foreground">{item.subtitulo}</span>
                    </button>
                  ))}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
}
